import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import type { ActionItem, ActionItemStatus } from '@/types';
import { ACTION_ITEM_STATUS_LABELS } from '@/types';
import { ActionItemStatusChip, EmptyState } from '@/components/common/Common';
import { formatDate, isOverdue, todayIso } from '@/utils/dateUtils';
import type { CreateActionItemInput } from '@/services/actionItemService';

const STATUS_OPTIONS: ActionItemStatus[] = ['open', 'in_progress', 'blocked', 'completed'];

interface ActionItemFormValue {
  title: string;
  owner: string;
  dueDate: string;
  status: ActionItemStatus;
}

const EMPTY_FORM: ActionItemFormValue = { title: '', owner: '', dueDate: todayIso(), status: 'open' };

export default function ActionItemsPanel({
  newsletterId,
  items,
  onCreate,
  onUpdate,
  onSetStatus,
  onDelete,
}: {
  newsletterId: string;
  items: ActionItem[];
  onCreate: (input: CreateActionItemInput) => void;
  onUpdate: (item: ActionItem) => void;
  onSetStatus: (item: ActionItem, status: ActionItemStatus) => void;
  onDelete: (item: ActionItem) => void;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null);
  const [form, setForm] = useState<ActionItemFormValue>(EMPTY_FORM);

  const openCreateDialog = () => {
    setEditingItem(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (item: ActionItem) => {
    setEditingItem(item);
    setForm({ title: item.title, owner: item.owner, dueDate: item.dueDate, status: item.status });
    setDialogOpen(true);
  };

  const handleSave = () => {
    if (!form.title.trim() || !form.owner.trim()) return;
    if (editingItem) {
      onUpdate({ ...editingItem, ...form });
    } else {
      onCreate({ newsletterId, ...form });
    }
    setDialogOpen(false);
  };

  const openItems = items.filter((item) => item.status !== 'completed');
  const completedItems = items.filter((item) => item.status === 'completed');

  const renderRow = (item: ActionItem) => (
    <ListItem
      key={item.id}
      divider
      secondaryAction={
        <Stack direction="row" spacing={1} alignItems="center">
          <Select
            size="small"
            value={item.status}
            onChange={(e) => onSetStatus(item, e.target.value as ActionItemStatus)}
            sx={{ minWidth: 140 }}
          >
            {STATUS_OPTIONS.map((status) => (
              <MenuItem key={status} value={status}>
                {ACTION_ITEM_STATUS_LABELS[status]}
              </MenuItem>
            ))}
          </Select>
          <Tooltip title="Delete">
            <IconButton edge="end" onClick={() => onDelete(item)} aria-label={`Delete ${item.title}`}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      }
    >
      <ListItemText
        primary={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography
              component="span"
              sx={{ cursor: 'pointer', fontWeight: 600 }}
              onClick={() => openEditDialog(item)}
            >
              {item.title}
            </Typography>
            <ActionItemStatusChip status={item.status} label={ACTION_ITEM_STATUS_LABELS[item.status]} />
            {item.carriedFromId && (
              <Tooltip title="Carried forward from a previous issue">
                <HistoryIcon fontSize="small" color="action" />
              </Tooltip>
            )}
          </Stack>
        }
        secondary={
          <Typography
            variant="body2"
            color={isOverdue(item.dueDate, item.status === 'completed') ? 'error' : 'text.secondary'}
          >
            {item.owner} · Due {formatDate(item.dueDate)}
            {isOverdue(item.dueDate, item.status === 'completed') ? ' · Overdue' : ''}
          </Typography>
        }
      />
    </ListItem>
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {openItems.length} open · {completedItems.length} completed
        </Typography>
        <Button startIcon={<AddIcon />} onClick={openCreateDialog} size="small">
          Add Action Item
        </Button>
      </Stack>

      {items.length === 0 ? (
        <EmptyState title="No action items yet" description="Add owners, due dates, and track follow-through." />
      ) : (
        <>
          <List dense>{openItems.map(renderRow)}</List>
          {completedItems.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h4" sx={{ mb: 1 }}>
                Completed
              </Typography>
              <List dense>{completedItems.map(renderRow)}</List>
            </>
          )}
        </>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>{editingItem ? 'Edit Action Item' : 'New Action Item'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              autoFocus
              fullWidth
            />
            <TextField
              label="Owner"
              value={form.owner}
              onChange={(e) => setForm({ ...form, owner: e.target.value })}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as ActionItemStatus })}
            >
              {STATUS_OPTIONS.map((status) => (
                <MenuItem key={status} value={status}>
                  {ACTION_ITEM_STATUS_LABELS[status]}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
